import type { JobSnapshot, JobStatus } from "@/types";

export const ACTIVE_STATUSES: ReadonlySet<JobStatus> = new Set([
  "queued",
  "running",
  "planning",
  "retrieving",
  "synthesizing",
  "verifying",
]);

// Iter 19 — disjoint + exhaustive complement to ACTIVE_STATUSES. The 10
// JobStatus values (ADR-091 §3.2) partition into ACTIVE_STATUSES ∪
// TERMINAL_STATUSES with EMPTY intersection. chatStore.loadJobFromHistory
// branches on isTerminal: terminal → snapshot hydration (NO SSE replay
// per iter 19 architectural reframing closing iter 18 defect 2),
// active → existing pumpJobReplay. Future cleanup that adds a new
// JobStatus value MUST update both sets to preserve the partition
// invariant (pinned by jobStatusBadge.test.ts).
export const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>([
  "completed",
  "failed",
  "cancelled",
  "dead_lettered",
]);

export function isActive(status: JobStatus): boolean {
  return ACTIVE_STATUSES.has(status);
}

export function isTerminal(status: JobStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function statusBadgeClass(status: JobStatus): string {
  if (status === "completed") return "bg-emerald-100 text-emerald-700";
  if (status === "failed" || status === "dead_lettered") return "bg-rose-100 text-rose-700";
  if (status === "cancelled") return "bg-zinc-100 text-zinc-600";
  return "bg-blue-100 text-blue-700";
}

export function truncate(text: string, max = 60): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

// Sidebar/palette label for a job: user-set title wins, else the truncated
// query text, else "Untitled". The outer trim collapses whitespace-only titles.
export function deriveDisplayLabel(job: JobSnapshot): string {
  return (job.title?.trim() || truncate(job.query_text) || "Untitled").trim();
}
